import { getBackupDomains, saveBackupDomains } from "./db";
import { probeRequestTest } from "./probe";

let gateResolved = false;
let resolveApiGate!: () => void;

export const apiGate = new Promise<void>(resolve => {
  resolveApiGate = resolve;
});

function releaseApiGate() {
  if (gateResolved) return;
  gateResolved = true;
  resolveApiGate();
}

// ====================================================================
// 代理状态（同步可读）：供页面组件决定是否走 SW 反代专用链路
// ====================================================================

let proxyActive = false;
type ProxyStateListener = (active: boolean) => void;
const proxyStateListeners = new Set<ProxyStateListener>();

function setProxyActive(next: boolean): void {
  if (proxyActive === next) return;
  proxyActive = next;
  proxyStateListeners.forEach(fn => {
    try {
      fn(next);
    } catch {
      // listener 异常不影响其它订阅
    }
  });
}

export function isProxyActive(): boolean {
  return proxyActive;
}

export function subscribeProxyState(listener: ProxyStateListener): () => void {
  proxyStateListeners.add(listener);
  return () => {
    proxyStateListeners.delete(listener);
  };
}

// 监听 SW 推回的 PROXY_STATE_CHANGED 广播
if (typeof navigator !== "undefined" && navigator.serviceWorker) {
  navigator.serviceWorker.addEventListener("message", evt => {
    const data = evt.data as { type?: string; proxyMode?: boolean } | null;
    if (data && data.type === "PROXY_STATE_CHANGED") {
      setProxyActive(!!data.proxyMode);
    }
  });
}

// 启动时主动拉一次状态（SW 可能因刷新/更新已经处于代理模式）
async function refreshProxyStateFromSw(): Promise<void> {
  try {
    const res = (await sendToSw({ type: "QUERY_PROXY_STATE" })) as
      | { proxyMode?: boolean }
      | { error?: string }
      | null;
    if (res && "proxyMode" in res) {
      setProxyActive(!!res.proxyMode);
    }
  } catch {
    // 查询失败保持默认 false
  }
}

function normalizeDomains(list: unknown): string[] {
  if (!Array.isArray(list)) return [];
  return Array.from(
    new Set(
      list
        .map(item =>
          String(item || "")
            .trim()
            .toLowerCase()
        )
        .filter(Boolean)
    )
  );
}

function getRuntimeBackupDomains(): string[] {
  return normalizeDomains(window._config?.backupDomains);
}

async function getFallbackList(): Promise<string[]> {
  const runtime = getRuntimeBackupDomains();
  if (runtime.length > 0) return runtime;
  return getBackupDomains();
}

// 主动申请持久化配额，降低 IndexedDB / Cache Storage 在长期失败站点上被淘汰的概率
async function requestPersistentStorage(): Promise<void> {
  try {
    if (
      typeof navigator !== "undefined" &&
      navigator.storage &&
      typeof navigator.storage.persist === "function"
    ) {
      await navigator.storage.persist();
    }
  } catch {
    // 申请失败不影响主流程
  }
}

// 通知 SW 进入代理模式（辅助手段：SW 自身在 fetch 失败时也会自动进入代理）
function notifySwEnterProxyMode(list: string[]): void {
  try {
    const controller = navigator.serviceWorker?.controller;
    if (controller) {
      controller.postMessage({ type: "ENTER_PROXY_MODE", backupDomains: list });
    }
  } catch {
    // SW 不可用时静默失败
  }
}

// 调试工具：在 DevTools Console 中调用
// __forceProxy('www.djdltest.com')  — 强制 SW 代理到指定备用域名
// __exitProxy()                     — 退出代理模式，恢复直连主域名
// __proxyState()                    — 查看当前代理状态
function sendToSw(msg: Record<string, unknown>): Promise<unknown> {
  return new Promise(resolve => {
    const controller = navigator.serviceWorker?.controller;
    if (!controller) {
      resolve({ error: "no SW controller" });
      return;
    }
    const channel = new MessageChannel();
    channel.port1.onmessage = e => resolve(e.data);
    setTimeout(() => resolve({ error: "timeout" }), 2000);
    controller.postMessage(msg, [channel.port2]);
  });
}

if (typeof window !== "undefined") {
  (window as any).__forceProxy = async (backupHost?: string) => {
    const host = backupHost || window._config?.backupDomains?.[0];
    if (!host) {
      console.error("请传入备用域名，如 __forceProxy('www.djdltest.com')");
      return;
    }
    const target = host.startsWith("http") ? host : `https://${host}`;
    const res = await sendToSw({ type: "FORCE_PROXY_MODE", target });
    console.log("[DomainGuard] 已强制进入代理模式 →", target, res);
    return res;
  };
  (window as any).__exitProxy = async () => {
    const res = await sendToSw({ type: "EXIT_PROXY_MODE" });
    console.log("[DomainGuard] 已退出代理模式，恢复直连", res);
    return res;
  };
  (window as any).__proxyState = async () => {
    const res = await sendToSw({ type: "QUERY_PROXY_STATE" });
    console.log("[DomainGuard] 当前状态:", res);
    return res;
  };
}

export async function bootstrapDomainGuard(): Promise<void> {
  // 全场景启用：普通 Web / PWA / InAppWebView 均执行容灾流程
  // 唯一前置：当前环境支持 Service Worker（否则容灾依赖的 SW 反代不可用，
  // 此时仍执行探活、但探活失败也无法切域 —— 等同于"尽力而为"）
  // if (!utils.updateCheckIsPwa()) {
  //   releaseApiGate();
  //   return;
  // }

  // PWA 场景下尽早申请持久化存储配额；不阻塞探活
  void requestPersistentStorage();

  // SW 可能已经因为上次刷新/更新而处于代理模式，主动拉一次状态同步给主线程
  void refreshProxyStateFromSw();

  const ok = await probeRequestTest();
  if (ok) {
    const domains = getRuntimeBackupDomains();
    if (domains.length > 0) {
      await saveBackupDomains(domains, location.origin);
    }
    releaseApiGate();
    return;
  }

  // 探活失败 → 确保备用域名列表写入存储（供 SW fetch fallback 读取）
  const list = await getFallbackList();
  if (list.length > 0) {
    await saveBackupDomains(list, location.origin);
    // 提前通知 SW，加速代理模式生效（非必须：SW fetch 失败时也会自行进入代理）
    notifySwEnterProxyMode(list);
    // 主线程也乐观置 true，避免在 SW 广播到达之前 GameDetail 走错链路
    setProxyActive(true);
  }

  // 立即释放 apiGate：业务请求发出后，SW 的 fetchWithFallback 会在 fetch 失败时
  // 自动进入代理模式并重试，不再依赖 postMessage 的时序
  releaseApiGate();
}
